import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import {
  createStaffMember,
  STAFF_DEPARTMENTS,
  STAFF_EMPLOYMENT_TYPES,
  STAFF_GENDERS,
  STAFF_STATUSES,
  updateStaffMember,
} from '../../../services/staffDirectoryService'

const inputClass =
  'mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-rapid-orange/30'

const EMPTY = {
  employeeId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  altPhone: '',
  gender: '',
  dateOfBirth: '',
  department: '',
  jobTitle: '',
  employmentType: 'full_time',
  status: 'active',
  hireDate: '',
  endDate: '',
  address: '',
  city: '',
  state: '',
  country: '',
  managerId: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelationship: '',
  avatarUrl: '',
  notes: '',
}

function toFormState(member) {
  if (!member) return { ...EMPTY }
  return {
    employeeId: member.employeeId || '',
    firstName: member.firstName || '',
    lastName: member.lastName || '',
    email: member.email || '',
    phone: member.phone || '',
    altPhone: member.altPhone || '',
    gender: member.gender || '',
    dateOfBirth: member.dateOfBirth || '',
    department: member.department || '',
    jobTitle: member.jobTitle || '',
    employmentType: member.employmentType || 'full_time',
    status: member.status || 'active',
    hireDate: member.hireDate || '',
    endDate: member.endDate || '',
    address: member.address || '',
    city: member.city || '',
    state: member.state || '',
    country: member.country || '',
    managerId: member.managerId ? String(member.managerId) : '',
    emergencyContactName: member.emergencyContactName || '',
    emergencyContactPhone: member.emergencyContactPhone || '',
    emergencyContactRelationship: member.emergencyContactRelationship || '',
    avatarUrl: member.avatarUrl || '',
    notes: member.notes || '',
  }
}

export default function StaffFormModal({ isOpen, onClose, member, staffOptions = [], onSaved }) {
  const [form, setForm] = useState(EMPTY)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')

  const isEdit = Boolean(member)

  useEffect(() => {
    if (!isOpen) return
    setForm(toFormState(member))
    setError('')
    setWorking(false)
  }, [isOpen, member])

  const managerChoices = useMemo(
    () => staffOptions.filter((option) => option.id !== member?.id),
    [staffOptions, member],
  )

  if (!isOpen) return null

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setWorking(true)
    setError('')

    const payload = {
      employee_id: form.employeeId.trim() || undefined,
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      alt_phone: form.altPhone.trim() || undefined,
      gender: form.gender || undefined,
      date_of_birth: form.dateOfBirth || undefined,
      department: form.department || undefined,
      job_title: form.jobTitle.trim() || undefined,
      employment_type: form.employmentType || 'full_time',
      status: form.status || 'active',
      hire_date: form.hireDate || undefined,
      end_date: form.endDate || undefined,
      address: form.address.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      country: form.country.trim() || undefined,
      manager_id: form.managerId ? Number(form.managerId) : undefined,
      emergency_contact_name: form.emergencyContactName.trim() || undefined,
      emergency_contact_phone: form.emergencyContactPhone.trim() || undefined,
      emergency_contact_relationship: form.emergencyContactRelationship.trim() || undefined,
      avatar_url: form.avatarUrl.trim() || undefined,
      notes: form.notes.trim() || undefined,
    }

    try {
      if (isEdit) {
        await updateStaffMember(member.id, payload)
      } else {
        await createStaffMember(payload)
      }
      onSaved?.()
      onClose()
    } catch (err) {
      const validation = err.response?.data?.errors
      const firstError = validation ? Object.values(validation)[0]?.[0] : null
      setError(firstError || err.response?.data?.message || 'Unable to save the staff member.')
    } finally {
      setWorking(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 bg-gray-900/50"
      />
      <form
        onSubmit={handleSubmit}
        className="relative z-10 my-8 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-bold text-gray-900">
            {isEdit ? 'Edit staff member' : 'Add staff member'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {error ? (
          <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="mt-4 space-y-6">
          <Section title="Personal information">
            <Field label="First name" required>
              <input type="text" value={form.firstName} onChange={set('firstName')} required className={inputClass} />
            </Field>
            <Field label="Last name" required>
              <input type="text" value={form.lastName} onChange={set('lastName')} required className={inputClass} />
            </Field>
            <Field label="Employee ID" hint="Leave blank to auto-generate">
              <input type="text" value={form.employeeId} onChange={set('employeeId')} placeholder="FR-0001" className={inputClass} />
            </Field>
            <Field label="Gender">
              <select value={form.gender} onChange={set('gender')} className={inputClass}>
                <option value="">Not specified</option>
                {STAFF_GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Date of birth">
              <input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} className={inputClass} />
            </Field>
            <Field label="Manager">
              <select value={form.managerId} onChange={set('managerId')} className={inputClass}>
                <option value="">No manager</option>
                {managerChoices.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}{option.employeeId ? ` (${option.employeeId})` : ''}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section title="Employment">
            <Field label="Department">
              <select value={form.department} onChange={set('department')} className={inputClass}>
                <option value="">Unassigned</option>
                {STAFF_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Job title">
              <input type="text" value={form.jobTitle} onChange={set('jobTitle')} className={inputClass} />
            </Field>
            <Field label="Employment type">
              <select value={form.employmentType} onChange={set('employmentType')} className={inputClass}>
                {STAFF_EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={set('status')} className={inputClass}>
                {STAFF_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Hire date">
              <input type="date" value={form.hireDate} onChange={set('hireDate')} className={inputClass} />
            </Field>
            <Field label="End date">
              <input type="date" value={form.endDate} onChange={set('endDate')} className={inputClass} />
            </Field>
          </Section>

          <Section title="Contact">
            <Field label="Email">
              <input type="email" value={form.email} onChange={set('email')} className={inputClass} />
            </Field>
            <Field label="Phone">
              <input type="text" value={form.phone} onChange={set('phone')} className={inputClass} />
            </Field>
            <Field label="Alternate phone">
              <input type="text" value={form.altPhone} onChange={set('altPhone')} className={inputClass} />
            </Field>
            <Field label="Avatar URL">
              <input type="url" value={form.avatarUrl} onChange={set('avatarUrl')} placeholder="https://…" className={inputClass} />
            </Field>
            <Field label="Address" wide>
              <input type="text" value={form.address} onChange={set('address')} className={inputClass} />
            </Field>
            <Field label="City">
              <input type="text" value={form.city} onChange={set('city')} className={inputClass} />
            </Field>
            <Field label="State">
              <input type="text" value={form.state} onChange={set('state')} className={inputClass} />
            </Field>
            <Field label="Country">
              <input type="text" value={form.country} onChange={set('country')} className={inputClass} />
            </Field>
          </Section>

          <Section title="Emergency contact">
            <Field label="Name">
              <input type="text" value={form.emergencyContactName} onChange={set('emergencyContactName')} className={inputClass} />
            </Field>
            <Field label="Phone">
              <input type="text" value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} className={inputClass} />
            </Field>
            <Field label="Relationship">
              <input type="text" value={form.emergencyContactRelationship} onChange={set('emergencyContactRelationship')} className={inputClass} />
            </Field>
          </Section>

          <Section title="Notes">
            <Field label="Notes" wide>
              <textarea rows={3} value={form.notes} onChange={set('notes')} className={inputClass} />
            </Field>
          </Section>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={working}
            className="rounded-lg bg-rapid-orange px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E84A15] disabled:opacity-60"
          >
            {working ? 'Saving…' : isEdit ? 'Save changes' : 'Add staff member'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

function Section({ title, children }) {
  return (
    <fieldset className="rounded-xl border border-gray-100 p-4">
      <legend className="px-1 text-xs font-bold uppercase tracking-wider text-gray-500">{title}</legend>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {children}
      </div>
    </fieldset>
  )
}

function Field({ label, hint, required, wide, children }) {
  return (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        {label}{required ? ' *' : ''}
      </span>
      {hint ? <span className="ml-2 text-[10px] font-normal normal-case text-gray-400">{hint}</span> : null}
      {children}
    </label>
  )
}
